(ns app.front.view.attachment.modal
  (:require [app.front.acceptance.events :as acceptance-evt]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.requirement.events :as req-events]
            [app.front.ui :as ui]
            [app.front.ui.modal :as ui-modal]
            [app.front.view.attachment.common :as attachment-common]
            [app.front.view.attachment.subs :as subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-event-db ::preview-clicked
  (fn [db [_ attachment-id version-id]]
    (assoc db ::show-preview-modal-for (util/mmap attachment-id version-id))))

(rf/reg-event-db ::close-preview-clicked
  (fn [db _]
    (dissoc db ::show-preview-modal-for)))

(rf/reg-sub ::preview-modal-data
  :-> ::show-preview-modal-for)

(rf/reg-sub ::show-preview-modal?
  :<- [::preview-modal-data]
  :-> some?)

(defn attachment-preview-modal [{:keys [tr]}]
  (when (<sub [::show-preview-modal?])
    (let [{:keys [attachment-id
                  version-id]}  (<sub [::preview-modal-data])
          file                  (<sub [::subs/latest-file-for-version version-id])
          content-type          (-> file :attachment.version.file/content-type)]
      [:div.attachment-preview
       [:div.attachment-view-header
        (-> file :attachment.version.file/filename)
        [:div.attachment-view-exit
         (cc/icon {:icon        i/remove
                   :description (tr [:close])
                   :class-name  "cursor-pointer"
                   :on-click    #(>evt [::close-preview-clicked])})]]
       [:div.iframe-container
        (let [img?    (str/starts-with? content-type "image/")
              video?  (str/starts-with? content-type "video/")
              element (cond
                        img? :img
                        video? :video
                        :else :iframe)
              url     (attachment-common/attachment-version-link attachment-id (:db/id file))
              params  {:src   url
                       :class (when (= content-type "text/plain") "bg-ui-light-gray px-4")}]
          (if video?
            [element {:controls true}
             [:source (merge params
                             {:type content-type})]]
            [element params]))]])))

(rf/reg-event-db ::acceptance-clicked
  (fn [db [_ id]]
    (assoc db ::show-acceptance-modal-for id)))

(rf/reg-event-db ::close-acceptance-clicked
  (fn [db _]
    (dissoc db ::show-acceptance-modal-for)))

(rf/reg-event-fx ::acceptance-changes-submitted
  (fn [{:keys [db]} [_ process-target-id latest-version-id latest-non-markup-version-id text review-id]]
    {:db (dissoc db ::show-acceptance-modal-for)
     :fx (cond-> [[:dispatch [::acceptance-evt/create
                              process-target-id
                              latest-version-id
                              :acceptance.value/needs-changes
                              text
                              review-id]]]
           ;; Current logic in the code focuses on latest non markup version
           ;; This means that latest-non-markup if not latest version, should also have the acceptance value.
           ;; In the future, we would want to only focus on latest version id
           (and latest-non-markup-version-id
                (not= latest-version-id latest-non-markup-version-id))
           (conj [:dispatch [::acceptance-evt/create
                             process-target-id
                             latest-non-markup-version-id
                             :acceptance.value/needs-changes
                             nil
                             review-id]]))}))

(rf/reg-sub ::acceptance-modal-data
  :-> ::show-acceptance-modal-for)

(rf/reg-sub ::show-acceptance-modal?
  :<- [::acceptance-modal-data]
  :-> some?)

(defn acceptance-modal [{:keys [tr process-target-id]}]
  (r/with-let [*acceptance-text (r/atom "")
               close-fn         (fn [_]
                                  (reset! *acceptance-text "")
                                  (>evt [::close-acceptance-clicked])
                                  true)]
    (let [id           (<sub [::acceptance-modal-data])
          on-submit-fn (if-not id
                         (constantly nil)
                         (let [latest-non-markup-version-id (<sub [::subs/latest-non-markup-version-id id])
                               latest-version-id            (<sub [::subs/latest-version-id id])
                               review-id                    (<sub [::acceptance-evt/review-id-for-new-acceptance latest-version-id])]
                           (fn [_]
                             (>evt [::acceptance-changes-submitted
                                    process-target-id
                                    latest-version-id
                                    latest-non-markup-version-id
                                    @*acceptance-text
                                    review-id])
                             (reset! *acceptance-text ""))))]
      [ui-modal/composed-input-modal
       {:open    (<sub [::show-acceptance-modal?])
        :data-cy "acceptance-modal"
        :key     "acceptance-modal"}
       (cc/modal-header
         {:button-on-click close-fn}
         [:h2 (tr [:acceptance.value/needs-changes])])
       (cc/modal-body
         {}
         [ui/text-area
          {:id          "attachment-acceptance-text"
           :data-cy     "attachment-acceptance-text"
           :label-text  (tr [:acceptance/request-text-field-title])
           :helper-text (tr [:acceptance/request-text-field-help])
           :value       @*acceptance-text
           :on-change   #(reset! *acceptance-text (-> % .-target .-value))
           :placeholder (tr [:acceptance/request-text-field-placeholder])}])
       (cc/modal-footer
         {:primary-button-text   (tr [:ok])
          :secondary-button-text (tr [:cancel])
          :on-request-submit     on-submit-fn
          :on-request-close      close-fn})])))

(rf/reg-event-db ::delete-clicked
  (fn [db [_ attachment-id]]
    {:pre [(integer? attachment-id)]}
    (assoc db ::show-delete-modal-for attachment-id)))

(rf/reg-event-db ::cancel-delete-clicked
  (fn [db _]
    (dissoc db ::show-delete-modal-for)))

(rf/reg-sub ::delete-modal-data
  :-> ::show-delete-modal-for)

(rf/reg-sub ::show-delete-modal?
  :<- [::delete-modal-data]
  :-> some?)

(rf/reg-event-fx ::confirm-delete-clicked
  (fn [_ [_ target-id attachment-id property? bond?]]
    (cond
      property?
      {:comm/command {:command    :property/delete-attachment
                      :method     :post
                      :data       {:property-id   target-id
                                   :attachment-id attachment-id}
                      :on-success [::entities/remove-from-entity-db]
                      :notify     {:success :attachments/delete-success}}}
      bond?
      {:comm/command {:command    :bond/delete-attachment
                      :method     :post
                      :data       {:bond-id       target-id
                                   :attachment-id attachment-id}
                      :on-success [::entities/remove-from-entity-db]
                      :notify     {:success :attachments/delete-success}}}
      :else
      {:dispatch [::process/signal
                  target-id
                  [:process.signal/attachment-delete
                   {:attachment-id attachment-id}]
                  {:notify {:success :attachments/delete-success}}]})))

(rf/reg-event-db ::unlock-clicked
  (fn [db [_ id unlock-kind]]
    (assoc db ::show-unlock-modal-for {:id          id
                                       :unlock-kind (or unlock-kind :attachment)})))

(rf/reg-event-db ::cancel-unlock-clicked
  (fn [db _]
    (dissoc db ::show-unlock-modal-for)))

(rf/reg-sub ::unlock-modal-data
  :-> ::show-unlock-modal-for)

(rf/reg-sub ::show-unlock-modal?
  :<- [::unlock-modal-data]
  :-> some?)

(rf/reg-event-fx ::confirm-unlock-clicked
  (fn [{:keys [db]} [_ process-target-id id unlock-kind]]
    {:db       (dissoc db ::show-unlock-modal-for)
     :dispatch [::process/signal
                process-target-id
                (if (= unlock-kind :review)
                  [:process.signal/review-unlock-from-external-tool
                   {:review-id id}]
                  [:process.signal/attachment-unlock-only-from-external-editing
                   {:attachment-id id}])
                {:on-success [::req-events/reload-application.success]}]}))

(defn delete-modal
  [{:keys [process-target-id tr property? bond?]}]
  (let [id (<sub [::delete-modal-data])]
    (when (or (<sub [::subs/can-delete-attachment? process-target-id id])
              property?
              bond?)
      [ui/confirm-modal
       {:open?        (<sub [::show-delete-modal?])
        :tr           tr
        :id           "delete-attachment-modal"
        :modal-title  (tr [:attachments/delete])
        :on-close     #(>evt [::cancel-delete-clicked])
        :on-yes-click #(>evt [::confirm-delete-clicked
                              process-target-id
                              id
                              property?
                              bond?])}
       (tr [:attachments/delete-confirm])])))

(defn unlock-modal
  [{:keys [process-target-id tr]}]
  (let [{:keys [id unlock-kind]} (<sub [::unlock-modal-data])
        attachment-unlock?       (= :attachment unlock-kind)
        review-unlock?           (= :review unlock-kind)]
    (when (or review-unlock?
              (and id (<sub [::subs/locked-for-editing-by id])))
      [ui/confirm-modal
       {:open?           (<sub [::show-unlock-modal?])
        :tr              tr
        :id              "unlock-attachment-modal"
        :modal-title     (tr [:attachments/unlock-confirm-title])
        :yes-button-text (tr [(if attachment-unlock? :attachments/unlock :review/unlock-review-attachments)])
        :no-button-text  (tr [:cancel])
        :on-close        #(>evt [::cancel-unlock-clicked])
        :on-yes-click    #(>evt [::confirm-unlock-clicked process-target-id id unlock-kind])}
       (tr [:attachments/unlock-confirm-subtitle])])))
